import { ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from '../../../../lib/i18n';
import { messageDir } from '../../../../lib/message-direction';
import { handleModalEscape, pushModalEscape } from '../../../../lib/modal-escape';
import { TASK_DETAILS_PAGE } from '../../../../lib/tasks-bi';
import { Markdown } from '../../../Markdown';
import { BackButton, Button, OverflowMenu, StatusBadge, ScrollArea, Typography } from '../../ui';
import { AgentConversationCard } from '../AgentConversationCard';
import { TaskActivityTimeline } from '../TaskActivityTimeline';
import { TaskFilesSection } from '../TaskFilesSection';
import { TaskSentOutreachSection } from '../TaskSentOutreachSection';
import { TaskChatOriginLine, TaskOriginLine } from '../TaskOriginLine';
import { TaskDecisionPrompts } from '../TaskDecisionPrompts';
import { TaskUserEdits } from '../TaskUserEdits';
import { useTaskDetailModal } from '../use-task-detail-modal';
import { CancelTaskConfirm } from '../CancelTaskConfirm';
import taskStyles from '../TaskDetailsPanel.module.css';
import styles from './TaskDetailMobile.module.css';

// The narrow task-details drill-in (Figma 12175-102348): a full-screen sheet rather than a centered
// card — a back control over the title + status badge + description on the standard grey surface, the
// agent-activity timeline on a white sunken band that fills the rest, and a pinned full-width
// "Resolve with {agent}" CTA. Always full-screen (it is only mounted below `useNarrow`, so unlike the
// shared Modal's `fullScreenOnSmall` there is no centered-card range to guard against). Behaviour, copy
// and analytics are the shared useTaskDetailModal — only the layout differs from the desktop modal.
export function TaskDetailMobile({ taskId }: { taskId: string }) {
  const vm = useTaskDetailModal(taskId);
  const { t } = useTranslation();

  const sheetRef = useRef<HTMLDivElement>(null);
  const entranceRef = useRef<Animation | null>(null);
  const closingRef = useRef(false);
  // Read the dismissal through a ref so a fresh handler each render doesn't re-register the escape stack.
  //
  // On a full-screen sheet the top-bar control IS a back control, not a close control — so when this
  // task was opened from a goal it goes BACK to that goal, exactly as a pushed screen does on both
  // phones, rather than dropping the reader out to the grid. That is not the "close became back" trap
  // the desktop card guards against: there is no ✕ here to overload, and the goal underneath keeps its
  // own way out. With nothing underneath it closes, as it always did.
  const closeRef = useRef(vm.close);
  closeRef.current = vm.back ? vm.back.onBack : vm.close;

  // Dismiss with a slide-down, then unmount. The openModal/closeModal slot owns the sheet's lifetime,
  // so it would otherwise vanish instantly — we play the exit first and call close() on finish. Held
  // in a ref (kept fresh each render) so the mount-once Escape listener always calls the latest one.
  // Reentrancy-guarded so Escape + a tap can't both fire. Reduced motion (or no element) closes at once.
  const requestCloseRef = useRef<() => void>(() => {});
  requestCloseRef.current = () => {
    if (closingRef.current) {
      return;
    }
    closingRef.current = true;
    const el = sheetRef.current;
    if (!el || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      closeRef.current();
      return;
    }
    // Start from wherever the sheet visually is (mid-open close doesn't snap to the top first); WAAPI
    // commits the start keyframe atomically, and `fill: 'forwards'` holds it off-screen until unmount.
    const from = getComputedStyle(el).transform;
    entranceRef.current?.cancel();
    const exit = el.animate([{ transform: from === 'none' ? 'translateY(0)' : from }, { transform: 'translateY(100%)' }], {
      duration: 260,
      easing: 'cubic-bezier(0.4, 0, 1, 1)',
      fill: 'forwards',
    });
    exit.onfinish = () => closeRef.current();
    exit.oncancel = () => closeRef.current();
  };

  // Slide up from the bottom on open, like a pushed screen. Driven by the Web Animations API rather than
  // a mount-time CSS transition: it commits the off-screen start keyframe atomically on the first frame
  // (no flash, no rAF/state race), runs on the compositor, and skips entirely under reduced-motion.
  useEffect(() => {
    const el = sheetRef.current;
    if (!el || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }
    const anim = el.animate([{ transform: 'translateY(100%)' }, { transform: 'translateY(0)' }], {
      duration: 300,
      easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
    });
    entranceRef.current = anim;
    return () => anim.cancel();
  }, []);

  // Claim Escape for this sheet the same way the DS Modal does (capture-phase, innermost-first) so it
  // closes — with the slide-down — without unwinding any panel underneath.
  useEffect(() => {
    const pop = pushModalEscape(() => requestCloseRef.current());
    const onKey = (e: KeyboardEvent) => handleModalEscape(e);
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      pop();
    };
  }, []);

  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div ref={sheetRef} className={styles.sheet} role="dialog" aria-modal="true" aria-labelledby="task-detail-title">
      <div className={styles.topBar}>
        <BackButton onClick={() => requestCloseRef.current()} />
        {/* Name the destination when there is one. A bare chevron on a stacked sheet cannot say whether
            it returns to the goal or leaves entirely, and that ambiguity is the reported bug one level
            down. */}
        {vm.back && <span className={styles.backToLabel}>{vm.back.label}</span>}
        {/* The task's other actions, at the top rather than stacked under the CTA: Cancel Task is
            destructive and belongs in a menu, and once it moved the footer had one button left. */}
        {vm.kind === 'ready' && vm.menu.length > 0 && (
          <div className={styles.topBarMenu}>
            <OverflowMenu
              ariaLabel={t('tasks.rowMenu.label', { title: vm.title })}
              actions={vm.menu.map((item) => ({
                key: item.key,
                label: item.label,
                destructive: item.destructive,
                onSelect: item.onClick,
              }))}
            />
          </div>
        )}
      </div>

      {vm.kind === 'error' ? (
        <div className={styles.stateRegion}>{t('tasks.detail.notFound')}</div>
      ) : vm.kind === 'loading' ? (
        <SheetShell
          title={
            <div className={styles.skelStack}>
              <Shimmer w="85%" h={22} />
              <Shimmer w="55%" h={22} />
            </div>
          }
          badge={<Shimmer w={96} h={20} r={8} />}
          desc={
            <div className={styles.skelStack}>
              <Shimmer w="100%" h={14} />
              <Shimmer w="80%" h={14} />
            </div>
          }
          activity={<TimelineSkeleton />}
          footer={<Shimmer w="100%" h={42} r={8} />}
        />
      ) : (
        <SheetShell
          titleId="task-detail-title"
          title={<Typography variant="h2">{vm.title}</Typography>}
          badge={<StatusBadge status={vm.task.status} detail notStarted={vm.notStarted} awaitingDecision={vm.awaitingDecision} />}
          desc={
            // The provenance line leads the body, above the description — same order as the list row,
            // where it sits above the title block. `awaitingDecision` keeps this block present even on
            // a task with no summary and no goal — rare, but a blocked task must never lose its question.
            vm.goalOrigin || vm.chatOrigin || vm.description || vm.awaitingDecision ? (
              <>
                {vm.goalOrigin && <TaskOriginLine origin={vm.goalOrigin} />}
                {/* No goal to name — the chat the task was born in, the origin every task has. */}
                {vm.chatOrigin && <TaskChatOriginLine onOpen={vm.onOpenChatOrigin} />}
                {vm.description && (
                  <div className={styles.desc}>
                    <Markdown variant="compact" dir={messageDir(vm.description)}>
                      {vm.description}
                    </Markdown>
                  </div>
                )}
                {/* The question this task is held on, answered in place — the drill-in's half of the
                    row's own chips. `TASK_DETAILS_PAGE` so a tap here is not misattributed to the list. */}
                <TaskDecisionPrompts task={vm.task} origin={vm.goalOrigin} onAnsweredChange={vm.onDecisionAnswered} page={TASK_DETAILS_PAGE} />
              </>
            ) : null
          }
          activity={
            <>
              <TaskActivityTimeline
                agentId={vm.task.agentId}
                agentName={vm.agentName}
                steps={vm.task.steps}
                currentStep={vm.currentStep}
                statusVariant={vm.statusVariant}
                isLive={vm.isLive}
              />
              <TaskSentOutreachSection outreaches={vm.sentOutreaches} />
              <TaskFilesSection files={vm.files} onOpen={vm.onOpenFile} />
              {/* The two writes the drill-in was missing: restate the task, and note on it. One shared
                  component with the desktop modal, so the two layouts cannot grow two different forms. */}
              <TaskUserEdits task={vm.task} editable={vm.editable} edits={vm.edits} notes={vm.notes ?? []} />
              <AgentConversationCard taskId={taskId} dashboardBuild={!!vm.task.isDashboardBuild} />
              <CancelTaskConfirm flow={vm.cancel} />
            </>
          }
          footer={
            <div className={styles.footerActions}>
              {/* The sheet's back chevron is its dismissal, so unlike the desktop modal there is no
                  "Close" to disambiguate here — the pinned CTA stands alone, and the task's other
                  actions live in the top bar's menu. */}
              <Button fullWidth size="large" onClick={vm.cta.onClick}>
                {vm.cta.label}
              </Button>
            </div>
          }
        />
      )}
    </div>,
    document.body,
  );
}

// The sheet's body layout — the same regions whether loading or ready, so the skeleton and the real
// content occupy identical structure and the swap between them never reflows.
function SheetShell({
  titleId,
  title,
  badge,
  desc,
  activity,
  footer,
}: {
  titleId?: string;
  title: ReactNode;
  badge: ReactNode;
  desc: ReactNode;
  activity: ReactNode;
  footer: ReactNode;
}) {
  return (
    <>
      <ScrollArea className={styles.scroll}>
        <div className={styles.header}>
          <div className={styles.title} id={titleId}>
            {title}
          </div>
          <div className={styles.statusStack}>
            {badge}
            {desc}
          </div>
        </div>
        <div className={styles.activity}>{activity}</div>
      </ScrollArea>
      <div className={styles.footer}>{footer}</div>
    </>
  );
}

function Shimmer({ w, h, r = 6 }: { w: number | string; h: number; r?: number }) {
  return <div className="orion-shimmer" style={{ width: w, height: h, borderRadius: r }} />;
}

// The activity placeholder — the same agent row + left-ruled step list the ready timeline draws.
function TimelineSkeleton() {
  return (
    <div className={taskStyles.activity}>
      <div className={taskStyles.agent}>
        <Shimmer w={15} h={15} r={8} />
        <Shimmer w={80} h={14} />
      </div>
      <div className={taskStyles.timeline}>
        {[0, 1, 2].map((i) => (
          <div key={i} className={taskStyles.step}>
            <Shimmer w={16} h={16} r={8} />
            <Shimmer w={i === 1 ? '70%' : '88%'} h={14} />
          </div>
        ))}
      </div>
    </div>
  );
}
